import { useZakeke } from '@zakeke/zakeke-configurator-react';
import { Button, Icon } from "components/Atomic";
import QuotationFormDialog from "components/dialogs/QuotationFormDialog";
import SaveDesignsDraftDialog from "components/dialogs/SaveDesignsDraftDialog";
import { T } from "Helpers";
import { TailSpin } from 'react-loader-spinner';
import useStore from "Store";
import styled from 'styled-components/macro';
import { ReactComponent as PdfSolid } from '../../assets/icons/file-pdf-solid.svg';
import { ReactComponent as SaveSolid } from '../../assets/icons/save-solid.svg';
import { ReactComponent as ShareSolid } from '../../assets/icons/share-alt-square-solid.svg';
import { MessageDialog, QuestionDialog, useDialogManager } from "../dialogs/Dialogs";
import ErrorDialog from "../dialogs/ErrorDialog";
import PdfDialog from "../dialogs/PdfDialog";
import ShareDialog from "../dialogs/ShareDialog";
import { CustomQuotationConfirmMessage, ExtensionFieldItem, ExtensionFieldsContainer, FooterContainer, FooterRightElementsContainer, PriceContainer, QuantityContainer } from "./LayoutStyled";
import NumericInput from "./NumericInput";

const PriceInfoTextContainer = styled.div`
  font-size: 14px;
`;

const Footer = () => {

    const { useLegacyScreenshot, setCameraByName, getPDF, addToCart, isAddToCartLoading, sellerSettings, product, price, quantity, setQuantity, eventMessages, extensionFields, saveComposition, createQuote
    } = useZakeke();

    const { setIsLoading, priceFormatter, isQuoteLoading, setIsQuoteLoading, isViewerMode, isDraftEditor, isEditorMode, customSizes, personalDetailsData } = useStore();
    const { showDialog, closeDialog } = useDialogManager();

    const handleAddToCart = () => {
        const cartMessage = eventMessages?.find(message => message.eventID === 4);
        if (cartMessage && cartMessage.visible && !isDraftEditor && !isEditorMode)
            showDialog('question', <QuestionDialog
                alignButtons='center'
                eventMessage={cartMessage?.description}
                buttonNoLabel={T._("Cancel", "Composer")}
                buttonYesLabel={T._("Add to cart", "Composer")}
                onYesClick={() => {
                    closeDialog('question');
                    addToCart([personalDetailsData, customSizes], undefined, useLegacyScreenshot);
                }} />)
        else {
            addToCart([personalDetailsData, customSizes], undefined, useLegacyScreenshot);
        }
    }

    const showError = (error: string) => {
        showDialog('error', <ErrorDialog error={error} onCloseClick={() => closeDialog('error')} />);
    }

    const handleShareClick = async () => {
        setCameraByName("buy_screenshot_camera", false, false);
        showDialog("share", <ShareDialog />);
    }

    const handleSaveClick = async () => {
        showDialog("save", <SaveDesignsDraftDialog onCloseClick={() => closeDialog('save')} />);
    }

    const handlePdfClick = async () => {
        try {
            setIsLoading(true);
            const url = await getPDF();
            showDialog("pdf", <PdfDialog url={url} onCloseClick={() => closeDialog('pdf')} />);

        } catch (ex) {
            console.error(ex);
            showError(T._("Failed PDF generation", "Composer"));
        } finally {
            setIsLoading(false);
        }
    }

    const handleSubmitRequestQuote = async (formData: any) => {
        try {
            closeDialog('request-quotation');
            setIsQuoteLoading(true);
            await saveComposition();
            await createQuote(formData);
            showDialog("message", <MessageDialog windowDecorator={CustomQuotationConfirmMessage} message={T._('Request for quotation sent successfully', 'Composer')} />);
            setIsQuoteLoading(false);
        }
        catch (ex) {
            console.error(ex);
            setIsQuoteLoading(false);
            showDialog("error", <ErrorDialog error={T._('An error occurred while sending request for quotation. Please try again.', 'Composer')}
                onCloseClick={() => closeDialog('error')} />);
        }
    }

    const handleGetQuoteClick = async () => {
        let rule = product?.quoteRule;
        if (rule)
            showDialog("request-quotation", <QuotationFormDialog onCloseClick={() => closeDialog('request-quotation')} getQuoteRule={rule} onFormSubmit={handleSubmitRequestQuote} />);
    }

    const buyVisibleForQuoteRule = product?.quoteRule ? product.quoteRule.allowAddToCart : true;

    return <FooterContainer>
        {(product && product.quantityRule) && <QuantityContainer>
            <label>{T._("Quantity", "Composer")}</label>
            <NumericInput
                value={quantity}
                readOnly
                onInput={(e: any) => setQuantity(parseFloat(e.currentTarget.value))}
                min={product.quantityRule.minQuantity != null ? Math.max(product.quantityRule.step || 1, product.quantityRule.minQuantity) : (product.quantityRule.step || 1)}
                max={product.quantityRule.maxQuantity != null ? product.quantityRule.maxQuantity : undefined}
                step={product.quantityRule.step != null ? product.quantityRule.step : undefined}
            />
        </QuantityContainer>}
        <FooterRightElementsContainer className="right-footer">

            {/* Extension Fields */}
            {extensionFields && <ExtensionFieldsContainer>
                {
                    extensionFields.map((extensionField: { name: string, value: number, label: string, formatString: string }, index) => {
                        return (
                            <ExtensionFieldItem key={index}>
                                <span>{extensionField.label}</span>
                                <div>
                                    {/* {formatString(extensionField.formatString, extensionField.value.toString())} */}
                                    {extensionField.value}
                                </div>
                            </ExtensionFieldItem>)
                    })}
            </ExtensionFieldsContainer>}

            {/* Price */}
            {(price !== null && price > 0 && (!sellerSettings || !sellerSettings.hidePrice)) && <PriceContainer>
                {priceFormatter.format(price)}
                {sellerSettings && sellerSettings.priceInfoText && <PriceInfoTextContainer dangerouslySetInnerHTML={{ __html: sellerSettings.priceInfoText }} />}
            </PriceContainer>}

            {/* PDF preview */}
            <Button
                key={"pdf"}
                onClick={() => handlePdfClick()}>
                <Icon><PdfSolid /></Icon>
            </Button>

            {/* Save compostition */}
            {!isDraftEditor && !isEditorMode && !isViewerMode && sellerSettings && sellerSettings.canSaveDraftComposition &&
                <Button
                    key={"save"}
                    onClick={() => handleSaveClick()}>
                    <Icon><SaveSolid /></Icon>
                </Button>
            }

            {/* Share */}
            {sellerSettings && sellerSettings.shareType !== 0 && !isEditorMode &&
                <Button
                    key={"share"}
                    onClick={() => handleShareClick()}>
                    <Icon><ShareSolid /></Icon>
                </Button>}

            {/* Get a quote */}
            {
                product?.quoteRule && !isViewerMode && !isDraftEditor && !isEditorMode &&
                <Button
                    key={"quote"}
                    primary
                    onClick={() => handleGetQuoteClick()}>
                    {isQuoteLoading && <TailSpin color="#FFFFFF" height="25px" />}
                    {!isQuoteLoading && <span>{T._("Get a quote", "Composer")}</span>}
                </Button>
            }

            {/* Add to cart */}
            {
                buyVisibleForQuoteRule && !isViewerMode &&
                <Button
                    key={"cart"}
                    primary
                    onClick={() => handleAddToCart()}>
                    {isAddToCartLoading && <TailSpin color="#FFFFFF" height="25px" />}
                    {!isAddToCartLoading && <span>{(isDraftEditor || isEditorMode) ? (T._("Save", "Composer")) : (T._("Add to cart", "Composer"))}</span>}
                </Button>
            }
        </FooterRightElementsContainer >
    </FooterContainer >;
}

export default Footer;